
import Q from 'q'
import { getErrorMessage } from '../../services/error-helper'
import {
    CONTENT_RECEIVE,
    CONTENT_REQUEST,
    ERROR_NOTIFICATION
} from '..'

const _contentLock = {}

class ContentLock {

    /**
     * Timestamp a request for a particular key, for later verification
     * @param key
     * @returns {number}
     */
    static request(key) {
        const stamp = Date.now()
        _contentLock[key] = stamp
        return stamp
    }

    /**
     * Check that a response to a request has the timestamp we expect for that particular key
     * @param key
     * @param {number} stamp
     * @returns {boolean} True if the timestamp matches
     */
    static receive(key, stamp) {
        const currentStamp = _contentLock[key]
        return currentStamp === stamp
    }

}

const requestContent = (key) => {
    return {
        key,
        type: CONTENT_REQUEST
    }
}

/**
 * Packs up the input parameters into an object and returns it. Returned object is ready to go to "dispatch()" with
 * type CONTENT_RECEIVE
 * @param key
 * @param success
 * @param content
 * @param response
 * @param [error]
 * @param [notificationType]
 * @param [receivedAt]
 * @returns {Object}
 */
const receiveContent = (key, success, content, response, error, notificationType, receivedAt) => {
    if (undefined === receivedAt) {
        receivedAt = Date.now()
    }

    const receiveContentObj = {
        content,
        key,
        receivedAt,
        response,
        success,
        error,
        type: CONTENT_RECEIVE,
    }

    if (notificationType) {
        receiveContentObj.notificationType = notificationType
    }

    return receiveContentObj
}

const processError = (dispatch, key, action, error, type, errorMap) => {
    if (!error.json) {
        error.json = () => {
            return Q({
                message: error.message,
                raw: error,
                stacktrace: error.stack
            })
        }
    }

    return error.json()
        .then(error => {
            const { raw: { response: { status } = {} } = {} } = error
            const displayMessage = getErrorMessage(status, errorMap)

            error.displayMessage = `Error ${status}: ${displayMessage}`

            return error
        })
        .then(error => dispatch(action(key, false, null, null, error, type)))
        .catch(e => {
            const json = {
                message: e.message,
                raw: error,
                stacktrace: e.stack
            }
            dispatch(action(key, false, null, null, json, type))
        })
}

/**
 * A thunk to be passed to dispatch(). Returns a function that will execute the call specified by actionObject.
 * @param actionObject
 * @returns {function: Promise} The return function will call the given service with the given parameters,
 *      and send the response data to the datastore. It has no return value.
 */
const fetchContent = (actionObject) => {
    const {
        params,
        path,
        resource,
        service,
        verb
    } = actionObject
    const key = actionObject.getKey()
    return dispatch => {
        const stamp = ContentLock.request(key)
        dispatch(requestContent(key))
        return service[resource][verb](path, params)
            .then((response) => {
                if (!response.headers) {
                    throw response
                }

                return {
                    content: response.data,
                    response
                }
            })
            .then((r) => {
                if (!ContentLock.receive(key, stamp)) {
                    return  // Stamp was not for the most recent request, do not use the results.
                }
                const { content, response } = r
                dispatch(receiveContent(key, true, content, response))  // store response
            })
            .catch((error) => {
                if (!ContentLock.receive(key, stamp)) {
                    return
                }

                let errorHandled = false
                if (typeof actionObject.onError === 'function') {
                    errorHandled = !!actionObject.onError(error)
                }

                if (typeof error.callback === 'function' && !errorHandled) {
                    errorHandled = error.callback()
                }

                return processError(dispatch, key, receiveContent, error, errorHandled ? '' : ERROR_NOTIFICATION, actionObject.errorMap)
            })
    }
}

/**
 * @param state
 * @param key
 * @returns {boolean} True if 1) information does NOT already exist in the state for this key, and
 *      2) we are not already fetching
 */
const shouldFetchContent = (state, key) => {
    const content = state.contentByKey[key]
    return !content && !state.isFetching
}

/**
 * Check an action object to be sure it contains a resource, verb and path.
 * @param {object} actionObject
 * @returns {undefined}
 * @throws {Error} If required members are not present, or are falsy
 */
const verifyAction = (actionObject) => {
    if (!actionObject.resource) {
        throw new Error('No resource in actionObject')
    }
    if (!actionObject.verb) {
        throw new Error('No verb in actionObject')
    }
    if (!actionObject.path) {
        throw new Error('No path in actionObject')
    }
}

/**
 * A thunk to be passed to dispatch(). Returns a function that will either:
 *      1) call fetchContent() (if information requested by the action has not yet been fetched)
 *      2) process the data (if fetch is finished), or
 *      3) do nothing (generally because we're fetching)
 * @param {Object} actionObject
 * @returns {function: Promise} The returned function will generally either 1) return another thunk to kick off
 *      the asynchronous operation or 2) dispatch the final action that concludes the asynchronous operation.
 * @throws {Error} if actionObject fails verification
 */
export function fetchContentIfNeeded(actionObject) {
    verifyAction(actionObject)

    return (dispatch, getState) => {
        const state = getState()
        const key = actionObject.getKey()
        if (shouldFetchContent(state, key) || actionObject.noCache) {
            return dispatch(fetchContent(actionObject))
        }

        if (!key.includes('me./me.') && !state.isFetching && !actionObject.noCache) {
            const response = state.contentByKey[key]
            const { content, error, success } = response

            // This call to dispatch() is the final step that uses an actual action rather than a thunk
            dispatch(receiveContent(key, success, content, response, error))
        }
    }
}

/**
 * Returns a function that will:
 *      1) delete the state data referenced by the actionObject, and
 *      2) kick off reprocessing of the (cleared) data.
 * @param {Object} actionObject
 * @returns {function}
 */
export function resetContent(actionObject) {
    verifyAction(actionObject)
    return (dispatch, getState) => {
        const state = getState()
        const key = actionObject.getKey()
        delete state.contentByKey[key]
        dispatch(receiveContent(key, true, {}, null, null))
    }
}



// WEBPACK FOOTER //
// src/actions/content/index.js